/**
 * 화면 지우기 — **표시 수위표**(이 브라우저의 렌더만 비운다).
 *
 * 지우기의 약속(confirm 문구)은 "내 화면에서만" 이다. 그래서 지우기는
 * 리듀서 상태(`state.items`)를 **건드리지 않는다**:
 *
 *  - 자막 복사·저장(transcript)은 `state.items` 를 읽으므로 지운 뒤에도
 *    **전체 기록**을 그대로 담는다. 상태를 비우면 복사본이 조용히
 *    잘리는 두 번째 결함이 된다.
 *  - 회의록 저장(§11.4)은 서버가 만들므로 애초에 영향이 없다.
 *  - 참가자 화면·서버 링버퍼도 그대로다(관리자 삭제 경로가 아니다).
 *
 * 무엇을 지우는가:
 *  - 지금 렌더된 확정 자막·gap·tombstone 을 id 로 표시해 렌더에서 뺀다.
 *  - **partial(진행 중 발화 자리표)은 지우지 않는다** — 그 발화의 확정
 *    자막은 지우기 **이후에** 도착하는 "새 자막" 이고, final 이 같은
 *    id(utt)를 승계하므로 자리표를 표시해 두면 새 자막까지 숨겨진다.
 *  - 새로 도착하는 자막은 새 id 라 정상 렌더된다.
 *
 * 지운 것은 어떻게 되돌아오는가:
 *  - **새로고침(재접속)으로만** 되돌아온다 — 서버 재생이 상태를 다시
 *    세운다. 지운 상태에서는 과거 스크롤 로드(§8.3)도 멈춘다: 히스토리는
 *    지운 범위보다 **더 오래된** 것만 가져오므로, 허용하면 지운 구간이
 *    조용한 구멍으로 남은 채 그 아래 옛 자막만 나타난다(§1 원칙 3 위반).
 *
 * gap 의 예외 — "조용한 유실 금지"(§1 원칙 3):
 *  - 리듀서는 인접한 client_gap 을 **기존 gap 에 병합**한다(id 유지,
 *    to_ts 연장). 지운 gap 에 지우기 이후의 유실이 병합되면 새 유실이
 *    숨겨지므로, 표시 시점의 to_ts 를 기억해 두고 **to_ts 가 자란 gap 은
 *    다시 보이게 한다**.
 */

import type { FeedItem } from './reducer'

/**
 * 지워진 항목 표시 — id → 표시 시점의 gap to_ts(gap 이 아니면 null).
 * null 이 아닌 값은 "이 to_ts 까지의 유실은 지웠다" 는 뜻이다.
 */
export type ClearMark = ReadonlyMap<string, number | null>

/** 지금 렌더된 항목을 지운 것으로 표시한다(반복 지우기는 누적). */
export function markCleared(
  items: readonly FeedItem[],
  prev: ClearMark | null,
): ClearMark {
  const next = new Map(prev ?? [])
  for (const item of items) {
    // partial 은 지우지 않는다 — final 이 같은 id 를 승계한다(위 주석).
    if (item.kind === 'partial') continue
    next.set(item.id, item.kind === 'gap' ? item.data.to_ts : null)
  }
  return next
}

/** 표시 수위표를 적용한 렌더 목록 — 표시가 없으면 원본을 그대로 쓴다. */
export function visibleFeed(
  items: readonly FeedItem[],
  mark: ClearMark | null,
): FeedItem[] {
  if (!mark || mark.size === 0) return items as FeedItem[]
  return items.filter((item) => {
    const clearedToTs = mark.get(item.id)
    if (clearedToTs === undefined) return true
    // 지운 gap 에 이후 유실이 병합돼 구간이 자랐다 — 유실은 숨기지 않는다.
    if (item.kind === 'gap' && clearedToTs !== null && item.data.to_ts > clearedToTs) {
      return true
    }
    return false
  })
}
